import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ListTodo, Sparkles, CheckCircle2, LifeBuoy, AlertTriangle, ReceiptText, HardDrive, Globe, Lightbulb, CalendarCheck, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const META = {
  leads: { icon: MessageSquare, tone: "#60a5fa" },
  tasks: { icon: ListTodo, tone: "#A3E635" },
  ai_requests: { icon: Sparkles, tone: "#38bdf8" },
  freigaben: { icon: CheckCircle2, tone: "#34d399" },
  support: { icon: LifeBuoy, tone: "#f472b6" },
  overdue_tasks: { icon: AlertTriangle, tone: "#f87171" },
  invoices: { icon: ReceiptText, tone: "#fbbf24" },
  storage_warnings: { icon: HardDrive, tone: "#fb923c" },
  website_warnings: { icon: Globe, tone: "#f87171" },
  approvals: { icon: Lightbulb, tone: "#a3e635" },
};

export default function AdminToday() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setData(null);
    setLoadError(false);
    api.get("/admin/today").then(({ data: result }) => {
      if (!Array.isArray(result?.buckets) || !result.buckets.every(b => b
        && typeof b.key === "string" && typeof b.label === "string"
        && Number.isSafeInteger(b.count) && b.count >= 0
        && typeof b.link === "string" && /^\/admin(?:\/|$)/.test(b.link))) {
        throw new Error("Incomplete daily overview");
      }
      if (active) setData(result);
    }).catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [attempt]);

  if (loadError) return <div className="fade-in" data-testid="admin-today">
    <PageHeader title={t("Heute wichtig")} subtitle={t("Kundenarbeit und Systemprüfungen getrennt im Blick.")} />
    <Card className="p-6" role="alert">
      <h2 className="text-lg font-bold text-white">{t("Tagesübersicht nicht verfügbar")}</h2>
      <p className="text-sm text-gray-300 mt-2">{t("Die Daten konnten nicht geladen werden. Der offene Arbeitsstand ist nicht verifiziert.")}</p>
      <button type="button" data-testid="admin-today-retry" onClick={() => setAttempt(n => n + 1)}
        className="mt-4 min-h-[44px] rounded-xl bg-[#A3E635] px-4 py-2 font-bold text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
        {t("Erneut versuchen")}
      </button>
    </Card>
  </div>;
  if (!data) return <Spinner />;

  const active = (data.buckets || []).filter((b) => b.count > 0);
  const security = data.system_security;
  const securityVerified = security?.verified === true
    && [security.critical, security.warn, security.open].every(n => Number.isSafeInteger(n) && n >= 0)
    && typeof security.last_check === "string" && Number.isFinite(Date.parse(security.last_check));

  return (
    <div className="fade-in space-y-5" data-testid="admin-today">
      <PageHeader title={t("Heute wichtig")} subtitle={t("Kundenarbeit und Systemprüfungen getrennt im Blick.")} />
      {user?.role === "super_admin" && <Card className="p-5 border border-amber-400/25" data-testid="today-system-security">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-white">{t("Betrieb & Sicherheit")}</h2>
            <p className="text-sm text-gray-300 mt-2" data-testid="today-security-status">
              {securityVerified
                ? t("{critical} kritisch · {warn} Warnungen · {open} offen")
                    .replace("{critical}", security.critical).replace("{warn}", security.warn).replace("{open}", security.open)
                : t("Nicht verifiziert")}
            </p>
            <p className="text-xs text-gray-400 mt-2">{t("Quelle: Sicherheit & QA")}
              {securityVerified && <> · <time dateTime={security.last_check}>{new Intl.DateTimeFormat(
                { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" }[lang] || "de-CH",
                { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Zurich" }
              ).format(new Date(security.last_check))}</time></>}
            </p>
          </div>
          <button type="button" data-testid="today-open-security" onClick={() => nav("/admin/qa")}
            className="min-h-[44px] rounded-xl border border-white/20 px-4 py-2 text-sm font-bold text-white hover:bg-white/5 focus-visible:outline focus-visible:outline-2">
            {t("Prüfpunkte öffnen")}
          </button>
        </div>
      </Card>}
      <h2 className="text-base font-bold text-white">{t("Kundenarbeit")}</h2>
      {active.length === 0 ? (
        <Card className="p-10 text-center" data-testid="admin-today-empty">
          <div className="w-16 h-16 rounded-2xl bg-[#A3E635]/10 border border-[#A3E635]/25 flex items-center justify-center mx-auto mb-5">
            <CalendarCheck className="w-8 h-8 text-[#A3E635]" />
          </div>
          <h3 className="text-xl font-black tracking-tight mb-1">{t("Keine offenen Kundenpunkte in dieser Übersicht.")}</h3>
          <p className="text-sm text-gray-400">{t("Diese Anzeige bezieht sich auf die für Sie sichtbaren Kundenbereiche.")}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {active.map((b) => {
            const m = META[b.key] || { icon: Sparkles, tone: "#A3E635" };
            const Icon = m.icon;
            return (
              <button key={b.key} onClick={() => nav(b.link)} data-testid={`today-card-${b.key}`}
                className="group text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:-translate-y-0.5 transition-all p-5">
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${m.tone}22`, color: m.tone }}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="text-3xl font-black tracking-tight" style={{ color: m.tone }}>{b.count}</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-gray-100">{t(b.label)}</span>
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
